<script setup>
import { computed } from "vue";
import { toParagraphCase } from "@/lib/toParagraphCase.js";
import TheMultiparagraph from "@/components/TheMultiparagraph.vue";
import TheButton from "@/components/TheButton.vue";

const props = defineProps([
  "title",
  "regions",
  "area",
  "experience",
  "employment",
  "schedule",
  "salaryOption",
  "minimalSalary",
  "maximalSalary",
  "description",
  "duties",
  "requirements",
  "proposal",
]);

function toMoney(value) {
  return value.toLocaleString("ru") + " ₽";
}

const salary = computed(() => {
  let salary;
  switch (props.salaryOption) {
    case "fixed":
      salary = props.minimalSalary || props.maximalSalary;
      return toMoney(salary);
    case "minimal":
      salary = props.minimalSalary || props.maximalSalary;
      return "от " + toMoney(salary);
    case "maximal":
      salary = props.maximalSalary || props.minimalSalary;
      return "до " + toMoney(salary);
    case "range":
      return (
        "от " +
        toMoney(props.minimalSalary) +
        " до " +
        toMoney(props.maximalSalary)
      );
    default:
      return "З/П не указана";
  }
});

const employment = computed(() => {
  const result = [];
  if (props.employment?.title) {
    result.push(props.employment.title);
  }
  if (props.schedule?.title) {
    result.push(props.schedule.title);
  }
  return result.length > 0 ? toParagraphCase(result.join(", ")) : undefined;
});

function handleRespond() {
  if (document.forms?.form740538760?.elements?.vacancy) {
    document.forms.form740538760.elements.vacancy.value = props.title;
  }
}
</script>

<template>
  <details :class="$style.wrapper" role="article">
    <summary :class="$style.header" role="heading">
      <h3 :class="$style.title">{{ props.title }}</h3>
    </summary>

    <div :class="$style.main">
      <h4 v-text="salary" style="white-space: nowrap" />
      <p
        v-if="props.experience && props.experience.title"
        v-text="toParagraphCase(props.experience.title)"
      />
      <p v-if="employment" v-text="employment" />

      <template v-if="props.description">
        <h4>Описание работы</h4>
        <TheMultiparagraph :content="props.description" />
      </template>

      <template v-if="props.duties">
        <h4>Обязанности</h4>
        <TheMultiparagraph :content="props.duties" />
      </template>

      <template v-if="props.requirements">
        <h4>Требования</h4>
        <TheMultiparagraph :content="props.requirements" />
      </template>

      <template v-if="props.proposal">
        <h4>Мы предлагаем</h4>
        <TheMultiparagraph :content="props.proposal" />
      </template>

      <TheButton
        @click="handleRespond"
        tag="a"
        href="#respond"
        role="button"
        aria-haspopup="dialog"
      >
        Откликнуться
      </TheButton>
    </div>
  </details>
</template>

<style module>
.wrapper {
  display: block;
  margin: 20px auto;
  border-radius: 8px;
  border: 1px solid #afb3b4;
  width: 100%;
}

.header {
  position: relative;
  list-style-type: none;
  padding: 30px 62px 30px 60px;
  cursor: pointer;
}

.title {
  font-size: 24px;
  font-weight: 400;
  color: #212529;
  transition: color 0.2s ease-in-out;
}

.header:hover .title {
  color: var(--color-red-base);
}

.header::after {
  content: "";
  position: absolute;
  top: calc(50% - 8px);
  right: 30px;
  display: block;
  width: 16px;
  height: 16px;
  background-position: center;
  background-repeat: no-repeat;
  background-size: auto;
  background-image: url("data:image/svg+xml,%3Csvg width='19' height='11' viewBox='0 0 19 11' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1.71838 1.61115L9.48077 9.37354L17.2432 1.61115' stroke='%23212529' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
  transform: rotate(0deg);
  transition: transform 0.2s ease-in-out;
}

.wrapper[open] .header::after {
  transform: rotate(180deg);
}

.main {
  padding: 30px 30px 50px 60px;
  border-top: 1px solid #afb3b4;
}

.main h4 {
  margin-top: 60px;
  margin-bottom: 30px;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.5;
}

.main h4:first-of-type {
  margin-top: 0;
}

.main p {
  margin-bottom: 20px;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
}

.main button {
  margin-top: 60px;
}

@media screen and (max-width: 960px) {
  .header {
    padding-left: 30px;
  }

  .title {
    font-size: 22px;
  }

  .main {
    padding-left: 30px;
  }

  .main p {
    font-size: 14px;
  }
}

@media screen and (max-width: 480px) {
  .header {
    padding: 20px;
  }

  .header::after {
    display: none;
  }

  .title {
    font-size: 20px;
  }

  .main {
    padding: 20px 20px 50px 20px;
  }

  .main p {
    font-size: 12px;
  }

  .main button {
    width: 100%;
  }
}
</style>
